import { useState, useEffect } from 'react';
import { getInvoices, createInvoice, updateInvoiceStatus, sendInvoiceReminder, type Invoice } from '../../api/invoices.api';
import { getCompanies, type Company } from '../../api/companies.api';

export default function Facturacion() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    clienteEmpresaId: '',
    monto: 0,
    fechaVencimiento: '',
    tipo: 'MENSUALIDAD' as const,
    concepto: '',
    status: 'PENDIENTE' as const
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invoicesData, companiesData] = await Promise.all([
        getInvoices(),
        getCompanies()
      ]);
      setInvoices(invoicesData);
      setCompanies(companiesData);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAGADA': return 'bg-green-100 text-green-700';
      case 'PENDIENTE': return 'bg-yellow-100 text-yellow-700';
      case 'VENCIDA': return 'bg-red-100 text-red-700';
      case 'ANULADA': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const filteredInvoices = invoices.filter(inv => 
    inv.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.cliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.nit.includes(searchTerm)
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await createInvoice(formData);
      setSuccessMessage('Factura creada exitosamente');
      setShowModal(false);
      loadData();
      setFormData({
        clienteEmpresaId: '',
        monto: 0,
        fechaVencimiento: '',
        tipo: 'MENSUALIDAD',
        concepto: '',
        status: 'PENDIENTE'
      });
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error creating invoice:', error);
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleMarkAsPaid = async (invoice: Invoice) => {
    if (!confirm('¿Estás seguro de marcar esta factura como PAGADA?')) return;
    try {
      await updateInvoiceStatus(invoice.id, 'PAGADA');
      setSuccessMessage('Factura marcada como pagada');
      loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error al actualizar el estado');
    }
  };

  const handleSendReminder = async (invoice: Invoice) => {
    try {
      await sendInvoiceReminder(invoice.id);
      setSuccessMessage(`Recordatorio enviado a ${invoice.cliente}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Error al enviar el recordatorio');
    }
  };

  return (
      <div className="space-y-6">
         {successMessage && (
          <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in">
            <i className="ri-check-line"></i>
            {successMessage}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Facturación SaaS</h1>
            <p className="text-slate-500 mt-1">Gestiona las facturas de las empresas clientes</p>
          </div>
          <button 
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition flex items-center gap-2"
          >
            <i className="ri-add-line"></i>
            Nueva Factura
          </button>
        </div>

        {/* Filtros */}
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input
              type="text"
              placeholder="Buscar por número, cliente o NIT..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Número</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Cliente / Plan</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Concepto</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Emisión</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Vencimiento</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Monto</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Estado</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                    No hay facturas registradas
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-slate-50 transition">
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-slate-800">
                      {invoice.numero}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-slate-800">{invoice.cliente}</p>
                        <p className="text-xs text-slate-500">{invoice.clienteEmpresa?.plan?.nombre || 'Sin Plan'}</p>
                      </div>
                    </td>
                     <td className="px-6 py-4 text-sm text-slate-600">
                      {invoice.concepto}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {new Date(invoice.fechaEmision).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {new Date(invoice.fechaVencimiento).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-bold text-slate-800">
                      formatCOP(invoice.monto)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                         {invoice.status === 'PENDIENTE' && (
                            <button 
                                onClick={() => handleMarkAsPaid(invoice)}
                                title="Marcar como Pagada"
                                className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                            >
                                <i className="ri-checkbox-circle-line text-lg"></i>
                            </button>
                         )}
                         {(invoice.status === 'PENDIENTE' || invoice.status === 'VENCIDA') && (
                            <button 
                                onClick={() => handleSendReminder(invoice)}
                                title="Enviar Recordatorio"
                                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            >
                                <i className="ri-mail-send-line text-lg"></i>
                            </button>
                         )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Modal Nueva Factura */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-slate-800">Nueva Factura</h2>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Empresa Cliente</label>
                  <select
                    value={formData.clienteEmpresaId}
                    onChange={(e) => setFormData({...formData, clienteEmpresaId: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
                    required
                  >
                    <option value="">Seleccionar empresa...</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre} (NIT: {c.nit})</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Monto</label>
                    <input
                      type="number"
                      value={formData.monto}
                      onChange={(e) => setFormData({...formData, monto: Number(e.target.value)})}
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Vencimiento</label>
                    <input
                      type="date"
                      value={formData.fechaVencimiento}
                      onChange={(e) => setFormData({...formData, fechaVencimiento: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Concepto</label>
                  <input
                    type="text"
                    value={formData.concepto}
                    onChange={(e) => setFormData({...formData, concepto: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
                    placeholder="Ej: Mensualidad Enero 2025"
                    required
                  />
                </div>

                <div>
                   <label className="block text-sm font-medium text-slate-700 mb-1">Tipo</label>
                   <select
                      value={formData.tipo}
                      onChange={(e) => setFormData({...formData, tipo: e.target.value as any})}
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
                   >
                     <option value="MENSUALIDAD">Mensualidad</option>
                     <option value="SERVICIO">Servicio Adicional</option>
                     <option value="MULTA">Multa</option>
                     <option value="OTRO">Otro</option>
                   </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button 
                    type="button" 
                    onClick={() => setShowModal(false)}
                    className="flex-1 py-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 font-medium"
                  >
                    Cancelar
                  </button>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="flex-1 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 font-medium disabled:opacity-70 flex justify-center gap-2"
                  >
                    {isSubmitting ? <i className="ri-loader-4-line animate-spin"></i> : 'Crear Factura'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
  );
}
